import { faCalendarCheck, faExternalLinkAlt, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Group,
    Loader,
    Modal,
    Stack,
    Table,
    Text,
    Title,
} from "@mantine/core";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppointmentsApi from "../../data/api/appointmentsApi";
import { IAssociation } from "../../types/association/Association";
import { IPerson } from "../../types/persons/person";
import { Role } from "../../types/persons/Role";
import AppointmentView from "../appointments/AppointmentView";

interface UnconfirmedAppointmentGroup {
    shiftSession: {
        _id: string;
        shiftId: string;
        shiftName: string;
        start: string;
        finish: string;
        venueId: string;
    };
    appointments: Array<{
        _id: string;
        person: string;
        rosteredAt: string;
        daysSinceRostered: number;
        start: string;
        type: string;
        details: {
            role: Role;
            court?: string;
        };
    }>;
}

interface UnconfirmedAppointmentsPageProps {
    selectedAssociation: IAssociation | null;
}

const UnconfirmedAppointmentsPage: React.FC<UnconfirmedAppointmentsPageProps> = ({
    selectedAssociation,
}) => {
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState<UnconfirmedAppointmentGroup[]>([]);
    const [persons, setPersons] = useState<Map<string, IPerson>>(new Map());
    const [error, setError] = useState<string | null>(null);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        loadUnconfirmedAppointments();
    }, [selectedAssociation]);


    const loadUnconfirmedAppointments = async () => {
        try {
            if (!selectedAssociation) return;
            setLoading(true);
            setError(null);

            const response = await AppointmentsApi.getUnconfirmedAppointments(
                selectedAssociation._id
            );

            if (response.success) {
                setGroups((response as any).groups || []);
                setTotalCount((response as any).totalCount || 0);
                setHasMore((response as any).hasMore || false);

                // Build person map
                const personMap = new Map<string, IPerson>();
                if ((response as any).persons) {
                    (response as any).persons.forEach((person: IPerson) => {
                        personMap.set(person._id, person);
                    });
                }
                setPersons(personMap);
            } else {
                setError("Failed to load unconfirmed appointments");
            }
        } catch (err) {
            console.error("Error loading unconfirmed appointments:", err);
            setError("Error loading data: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateStr: string) => {
        const dt = DateTime.fromISO(dateStr);
        return dt.toLocaleString(DateTime.DATETIME_MED);
    };

    const formatTimeUntil = (dateStr: string) => {
        const dt = DateTime.fromISO(dateStr);
        const now = DateTime.now();
        const diff = dt.diff(now, ["days", "hours"]).toObject();

        if (diff.days && diff.days >= 1) {
            return `in ${Math.floor(diff.days)} day${Math.floor(diff.days) !== 1 ? "s" : ""}`;
        } else if (diff.hours && diff.hours >= 1) {
            return `in ${Math.floor(diff.hours)} hour${Math.floor(diff.hours) !== 1 ? "s" : ""}`;
        } else {
            return "very soon";
        }
    };

    const getDaysSinceRosteredBadgeColor = (days: number) => {
        if (days >= 7) return "red";
        if (days >= 3) return "orange";
        return "blue";
    };

    const getPersonName = (personId: string) => {
        const person = persons.get(personId);
        if (!person) return "Unknown";
        return `${person.preferredName || person.firstName} ${person.lastName}`;
    };

    const totalUnconfirmed = groups.reduce(
        (sum, group) => sum + group.appointments.length,
        0
    );

    if (!selectedAssociation) {
        return (
            <Container>
                <Stack align="center" mt="xl">
                    <Text>Please select an association to view unconfirmed appointments.</Text>
                </Stack>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container>
                <Stack align="center" mt="xl">
                    <Loader size="lg" />
                    <Text>Loading unconfirmed appointments...</Text>
                </Stack>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <div>
                        <Title order={2}>
                            <FontAwesomeIcon icon={faCalendarCheck} /> Unconfirmed Appointments
                        </Title>
                        <Text size="sm" c="dimmed">
                            Published appointments awaiting confirmation from officials
                        </Text>
                    </div>
                    <Badge size="lg" variant="filled" color={totalUnconfirmed > 0 ? "orange" : "green"}>
                        {totalUnconfirmed} unconfirmed
                    </Badge>
                </Group>

                {hasMore && (
                    <Alert color="yellow" title="Result Limit Reached">
                        Showing the first {totalCount} appointments. There are more unconfirmed appointments beyond this limit.
                        Consider filtering by association or contacting administrators if you need to see all data.
                    </Alert>
                )}

                {error && (
                    <Alert color="red" title="Error">
                        {error}
                    </Alert>
                )}

                {!loading && groups.length === 0 && (
                    <Alert color="green" title="All Clear!" icon={<FontAwesomeIcon icon={faCalendarCheck} />}>
                        No unconfirmed appointments found. All future appointments have been confirmed!
                    </Alert>
                )}

                {groups.map((group) => {
                    const sessionStart = DateTime.fromISO(group.shiftSession.start);
                    const isPast = sessionStart < DateTime.now();
                    const rosterUrl = `/roster/${group.shiftSession.shiftId}/${group.shiftSession._id}`;

                    return (
                        <Card key={group.shiftSession._id} shadow="sm" padding="lg" withBorder>
                            <Stack gap="sm">
                                <Group justify="space-between">
                                    <div>
                                        <Group gap="xs">
                                            <Title order={4}>{group.shiftSession.shiftName}</Title>
                                            <Badge color={isPast ? "red" : "blue"}>
                                                {formatTimeUntil(group.shiftSession.start)}
                                            </Badge>
                                            <Button
                                                component={Link}
                                                to={rosterUrl}
                                                size="xs"
                                                variant="light"
                                                leftSection={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                                            >
                                                View Roster
                                            </Button>
                                        </Group>
                                        <Text size="sm" c="dimmed">
                                            {formatDateTime(group.shiftSession.start)}
                                        </Text>
                                    </div>
                                    <Badge size="lg" color="orange">
                                        {group.appointments.length} unconfirmed
                                    </Badge>
                                </Group>

                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Official</Table.Th>
                                            <Table.Th>Role</Table.Th>
                                            <Table.Th>Court</Table.Th>
                                            <Table.Th>Rostered</Table.Th>
                                            <Table.Th>Actions</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {group.appointments.map((appointment) => {
                                            const person = persons.get(appointment.person);
                                            return (
                                                <Table.Tr key={appointment._id}>
                                                    <Table.Td>
                                                        {person ? (
                                                            <Link to={`/u/${person.externalIdentifier}`}>
                                                                {getPersonName(appointment.person)}
                                                            </Link>
                                                        ) : (
                                                            <Text c="dimmed">Unknown</Text>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge variant="light">
                                                            {appointment.details?.role ? Role.toString(appointment.details?.role) : appointment.type}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {appointment.details?.court || <Text c="dimmed">-</Text>}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm">
                                                            {DateTime.fromISO(appointment.rosteredAt).toLocaleString(
                                                                DateTime.DATE_MED
                                                            )}
                                                            <Badge
                                                                style={{ marginLeft: 8 }}
                                                                color={getDaysSinceRosteredBadgeColor(
                                                                    appointment.daysSinceRostered
                                                                )}
                                                            >
                                                                {appointment.daysSinceRostered === 0
                                                                    ? "Today"
                                                                    : appointment.daysSinceRostered === 1
                                                                        ? "1 day"
                                                                        : `${appointment.daysSinceRostered} days`}
                                                            </Badge>
                                                        </Text>

                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            leftSection={<FontAwesomeIcon icon={faEye} />}
                                                            onClick={() => {
                                                                setSelectedAppointmentId(appointment._id);
                                                                setModalOpen(true);
                                                            }}
                                                        >
                                                            View
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>

            <Modal
                opened={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedAppointmentId(null);
                }}
                title="Appointment Details"
                size="xl"
            >
                {selectedAppointmentId && (
                    <AppointmentView
                        appointmentId={selectedAppointmentId}
                        disableEdits={false}
                        venues={{}}
                    />
                )}
            </Modal>
        </Container>
    );
};

export default UnconfirmedAppointmentsPage;
